import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAddItemSetQuestionConditions } from '../add-item-set-question-conditions-provider';

import {
  AddItemSetQuestionConditionsSchema,
  type AddItemSetQuestionConditionsFormType,
} from './add-item-set-question-conditions-schema';
import { getDefaultTopicsAndSubTopics } from './utils';

interface AddItemSetQuestionConditionsFormProviderProps extends PropsWithChildren {
  onQuestionSizeChange: (size: number | undefined) => void;
  initialValues?: AddItemSetQuestionConditionsFormType;
}
export default function AddItemSetQuestionConditionsFormProvider(
  props: Readonly<AddItemSetQuestionConditionsFormProviderProps>,
) {
  const { children, initialValues, onQuestionSizeChange } = props;
  const { chapterData, levelItemsWithLevel, checkExistOnStructures } =
    useAddItemSetQuestionConditions();

  const { topics: defaultTopics, subTopics: defaultSubTopics } = useMemo(() => {
    return getDefaultTopicsAndSubTopics({
      levelItems: levelItemsWithLevel,
      checkExistOnStructures,
      chapterRefId: chapterData.refId,
      initialValues,
    });
  }, [levelItemsWithLevel, checkExistOnStructures, chapterData, initialValues]);

  const formContext = useForm<AddItemSetQuestionConditionsFormType>({
    defaultValues: initialValues ?? {
      location: {
        topics: {},
        subtopics: {},
      },
      los: [],
      conditions: [
        {
          properties: [],
        },
      ],
    },
    resolver: zodResolver(AddItemSetQuestionConditionsSchema),
  });

  const { watch, reset } = formContext;
  const { contentType } = watch();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'contentType') {
        reset({
          ...value,
          los: [],
          location: { topics: defaultTopics, subtopics: defaultSubTopics },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, defaultTopics, defaultSubTopics]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'itemSetType') {
        onQuestionSizeChange(parseInt(value.itemSetType as unknown as string) ?? undefined);
      }

      if (name === 'itemSetType' || name === 'contentType') {
        reset({
          ...value,
          los: [],
          location: { topics: defaultTopics, subtopics: defaultSubTopics },
          conditions: [{ properties: [] }],
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onQuestionSizeChange, defaultTopics, defaultSubTopics]);

  useEffect(() => {
    if (levelItemsWithLevel.length) {
      formContext.reset({
        ...formContext.getValues(),
        location: {
          topics: defaultTopics,
          subtopics: defaultSubTopics,
        },
      });
    }
  }, [levelItemsWithLevel, contentType, defaultTopics, defaultSubTopics]);

  return <FormProvider {...formContext}>{children}</FormProvider>;
}
